/**
 * One error tracking configuration, shared by the browser and the Lambda.
 *
 * The app ships Sentry's SDK and nothing else. Better Stack's error tracking
 * accepts Sentry SDK events, so a stage backed by Better Stack differs only in
 * the DSN it was handed — which means this module never branches on the
 * provider, and neither does anything that calls it. `infra/error-tracking.ts`
 * decides which service the DSN belongs to; docs/error-tracking.md lists what
 * each of them then does with the events.
 *
 * Everything here is pure so it can be tested. The two `sentry.*.config.ts`
 * files at the root of this app are the only callers, and they do nothing but
 * hand the result to `Sentry.init`.
 */

import type { ErrorEvent, EventHint } from "@sentry/astro";

export type ErrorTrackingProvider = "betterstack" | "none" | "sentry";

export interface ErrorTrackingEnvironment {
  /** `PUBLIC_ERROR_TRACKING_DSN`. Empty when the stage tracks nothing. */
  dsn?: string;
  /** `PUBLIC_ERROR_TRACKING_PROVIDER`. */
  provider?: string;
  /** `PUBLIC_ERROR_TRACKING_RELEASE` — the deployed commit, or empty. */
  release?: string;
  /** `PUBLIC_STAGE`. Becomes the Sentry environment. */
  stage?: string;
}

export interface ErrorTrackingSettings {
  dsn: string;
  environment: string;
  /** Omitted rather than empty when the commit is unknown. */
  release?: string;
  /** Fraction of *error* events kept. Errors are rare; keep all of them. */
  sampleRate: number;
}

/** `import.meta.env` values are strings; this is what "unset" looks like. */
const UNSET = "";

/**
 * Errors that say nothing about this site.
 *
 * Every one of these is generated by something other than our code running in
 * a browser we do not control, and each of them is capable of out-numbering
 * real errors on its own. They are dropped before sending rather than filtered
 * in the dashboard, because an event that is never sent costs no quota.
 */
const IGNORED_MESSAGES = [
  // Fired when a resize handler does not settle within one animation frame.
  // Benign by specification, and browsers report it as an uncaught error.
  "ResizeObserver loop",
  // A navigation or a `client:visible` island unmounting mid-request. The user
  // has already moved on; there is nothing to fix.
  "AbortError",
  "The operation was aborted",
  // Safari's wording for a request the user's own network or extension killed.
  "Load failed",
  "NetworkError when attempting to fetch resource",
  // Injected by browser extensions and by in-app webviews, not by this site.
  "chrome-extension://",
  "moz-extension://",
  "safari-extension://",
  "webkit-masked-url",
];

/**
 * Query parameters carrying something we must not store.
 *
 * `preview` is the draft preview token from infra/secrets.ts. It unlocks every
 * unpublished article for a week, and it travels in the URL — so any error on
 * a previewed page would otherwise file the working token into a third-party
 * service, in a field designed to be read by whoever triages the issue.
 */
const REDACTED_QUERY_PARAMS = ["preview", "token"];

const REDACTED = "[redacted]";

/**
 * The settings for this environment, or `undefined` to leave tracking off.
 *
 * Off is not an error. A stage can be deliberately untracked
 * (infra/error-tracking-provider.ts), and a bare `bun run build` outside SST
 * has no environment at all; both must build and run, silently, rather than
 * failing on a missing DSN the way a missing Resend key does. The difference
 * is that a dropped error is recoverable from the logs and a dropped Enquiry
 * is not.
 */
export function errorTrackingSettings(
  env: ErrorTrackingEnvironment
): ErrorTrackingSettings | undefined {
  const dsn = env.dsn?.trim() ?? UNSET;
  if (dsn === UNSET) {
    return;
  }

  const release = env.release?.trim() ?? UNSET;

  return {
    dsn,
    // Named for the stage, so one Sentry project or Better Stack application
    // per stage still separates `sst dev` traffic from anything a visitor did.
    environment: env.stage?.trim() || "unknown",
    ...(release === UNSET ? {} : { release }),
    // Errors are the product. Sampling them would mean the count in the
    // dashboard no longer answers "how many people hit this", which is the
    // question it exists to answer.
    //
    // There is deliberately no `tracesSampleRate` here, and no tracing
    // integration in either init file. Tracing is a second product: a span per
    // request rather than an event per failure, metered separately by both
    // providers, and nothing anybody asked this integration for.
    //
    // Leaving the integration out is not on its own a page-weight argument,
    // which is worth saying because it looks like one: dropping it from the
    // client bundle measured 57 bytes. The browser SDK is one module graph and
    // the error handlers pull in nearly all of it either way. What actually
    // removes the code is `excludeTracing` in the `bundleSizeOptimizations`
    // block in astro.config.ts, and the two belong together — keeping this
    // absent without that set would ship tracing to every page and use none of
    // it. Measured numbers are in docs/error-tracking.md.
    sampleRate: 1,
  };
}

/**
 * The same settings, read from `import.meta.env`.
 *
 * The four variables are read in exactly one place. Both init files need them
 * and neither can share a module-level constant — one is bundled for the
 * browser and the other for the Lambda — so without this the identical block
 * was written twice, which is one place for a typo to hide in a value that
 * silently turns reporting off.
 *
 * Kept apart from `errorTrackingSettings`, which stays pure and takes its
 * environment as a parameter, so the sampling and the release handling can be
 * tested without a build step.
 */
export function errorTrackingSettingsFromEnv():
  | ErrorTrackingSettings
  | undefined {
  return errorTrackingSettings({
    dsn: import.meta.env.PUBLIC_ERROR_TRACKING_DSN,
    provider: import.meta.env.PUBLIC_ERROR_TRACKING_PROVIDER,
    release: import.meta.env.PUBLIC_ERROR_TRACKING_RELEASE,
    stage: import.meta.env.PUBLIC_STAGE,
  });
}

/** Whether the environment asked for tracking at all. Reporting only. */
export function errorTrackingProvider(
  env: ErrorTrackingEnvironment
): ErrorTrackingProvider {
  const provider = env.provider?.trim();
  if (provider === "sentry" || provider === "betterstack") {
    return provider;
  }
  return "none";
}

/**
 * Default integrations removed from the server SDK.
 *
 * Both of them take what the *app* did rather than what the request carried,
 * which is why `sendDefaultPii: false` does not cover them:
 *
 * - `Console` turns every `console.*` call into a breadcrumb with the
 *   arguments attached verbatim, and instruments the console specifically when
 *   it sees `LAMBDA_TASK_ROOT` — so it is on exactly where this app runs. The
 *   Enquiry endpoint logs the whole Enquiry on a delivery failure and reports
 *   that failure on the next line; left on, the report carries the Prospect's
 *   name, email, phone and message.
 * - `LocalVariables` attaches each frame's locals to an uncaught exception. On
 *   a route that has just parsed an Enquiry, those locals are the Enquiry.
 *
 * Two names for the second one, because the SDK registers a different
 * integration depending on the Node version. Naming only `LocalVariables`
 * leaves `LocalVariablesAsync` running, and nothing says so — the events just
 * quietly carry more than they should.
 */
const REMOVED_SERVER_INTEGRATIONS = new Set([
  "Console",
  "LocalVariables",
  "LocalVariablesAsync",
]);

/**
 * The SDK's server defaults, minus the ones that carry personal data.
 *
 * Takes the defaults and returns a subset, rather than returning a fixed list:
 * an integration added in a future SDK version arrives on its own, and only
 * the named ones are ever dropped.
 *
 * Matched on `Integration.name`, the runtime name, which is not always the
 * name of the factory that built it. Here rather than inline in
 * `sentry.server.config.ts` so the names can be asserted — a typo in one is
 * invisible at runtime, and the thing it lets through is an Enquiry.
 */
export function withoutPiiIntegrations<T extends { name: string }>(
  defaults: T[]
): T[] {
  return defaults.filter(
    (integration) => !REMOVED_SERVER_INTEGRATIONS.has(integration.name)
  );
}

/**
 * True for noise that should never leave the browser.
 *
 * Matched against the message and the type, because the same interruption
 * surfaces as `AbortError` in one browser and as a bare string in another.
 */
export function isIgnoredError(input: {
  message?: string;
  type?: string;
}): boolean {
  const haystack = `${input.type ?? ""} ${input.message ?? ""}`;
  return IGNORED_MESSAGES.some((needle) => haystack.includes(needle));
}

/**
 * Strip a URL of anything that is a credential.
 *
 * Returns the input unchanged when it is not a URL — a stack frame's `file`
 * is often a bare path, and mangling it would break source map lookup.
 */
export function redactUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }

  let changed = false;
  for (const name of REDACTED_QUERY_PARAMS) {
    if (parsed.searchParams.has(name)) {
      parsed.searchParams.set(name, REDACTED);
      changed = true;
    }
  }

  return changed ? parsed.toString() : url;
}

/**
 * The last thing that runs before an event is sent.
 *
 * Two jobs, in this order: drop the events that are not about this site, and
 * take the personal data out of the ones that are. The second matters more
 * than it looks. The only form on this site collects a Prospect's name, email,
 * phone and the message they wrote — so an error raised anywhere near
 * `/api/enquiry` is one careless default away from copying an Enquiry into a
 * third-party service that the privacy policy does not name.
 *
 * `sendDefaultPii` is off in both init files, which handles the request body
 * and the IP address. This handles what the SDK cannot know is sensitive: the
 * preview token in the URL, and the cookies.
 */
export function scrubEvent(
  event: ErrorEvent,
  hint?: EventHint
): ErrorEvent | null {
  const exception = event.exception?.values?.at(0);
  const message =
    exception?.value ??
    event.message ??
    (hint?.originalException instanceof Error
      ? hint.originalException.message
      : undefined);

  if (isIgnoredError({ message, type: exception?.type })) {
    return null;
  }

  if (event.request) {
    const { headers, url } = event.request;
    event.request = {
      ...event.request,
      // Dropped whole rather than filtered. The set of cookies this site can
      // see is not fixed — consent, PostHog and the draft preview all write
      // one — and none of them helps anybody read a stack trace.
      cookies: undefined,
      ...(headers ? { headers: redactHeaders(headers) } : {}),
      ...(url ? { url: redactUrl(url) } : {}),
    };
  }

  // Where the browser was, which is a URL like any other.
  for (const crumb of event.breadcrumbs ?? []) {
    if (typeof crumb.data?.to === "string") {
      crumb.data.to = redactUrl(crumb.data.to);
    }
    if (typeof crumb.data?.from === "string") {
      crumb.data.from = redactUrl(crumb.data.from);
    }
  }

  return event;
}

/**
 * Headers, minus the ones that authenticate somebody.
 *
 * `cookie` carries the PostHog distinct id and the draft preview cookie;
 * `authorization` should never be set against this site, and if it ever is,
 * the error report is not where it should show up.
 */
function redactHeaders(headers: Record<string, string>) {
  const redacted: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    const key = name.toLowerCase();
    redacted[name] =
      key === "cookie" || key === "authorization" || key === "set-cookie"
        ? REDACTED
        : value;
  }
  return redacted;
}
